import { v4 as uuidv4 } from 'uuid';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getMediaElementByUuid from 'editor/src/store/design/selector/getMediaElementByUuid';
import getStructureIndexByElementUuid from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { MediaText } from 'editor/src/store/design/types';
import applyValueFormat from 'editor/src/store/design/util/applyValueFormat';
import buildSharedTextValueUpdate from 'editor/src/store/design/util/buildSharedTextValueUpdate';
import {
  getPersonalizationTextUuids,
  getSharedTextFieldMembers,
  isInReflowRow,
  receivesSharedValue,
} from 'editor/src/store/design/util/sharedTextFields';
import isSampleFlagChangeAllowed from 'editor/src/store/editor/selector/isSampleFlagChangeAllowed';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';

import syncSharedTextValueOperation from './syncSharedTextValueOperation';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

/**
 * Ties a personalisable text to `targetUuid` so the two hold one value, or
 * releases it from whatever group it is in when `targetUuid` is undefined.
 *
 * Joining adopts the target's current string, and the format that string was
 * poured through. Two layers claiming to be the same field must not sit there
 * showing different text: the author would have no way to tell which one the
 * buyer's first keystroke is going to win. The format comes along because the
 * value is stored formatted — a group that disagreed about it would keep
 * whichever the buyer's page applied, and print that on the other side.
 */
const updateSharedTextFieldOperation =
  (elementUuid: number, targetUuid: number | undefined): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const { designData } = state.design;
    const element = getMediaElementByUuid(state, elementUuid) as MediaText | undefined;
    const address = getStructureIndexByElementUuid(state, elementUuid);
    if (!element || element.type !== 'text' || !address) {
      return;
    }

    const currentId = element.extra.sharedFieldId;
    const target =
      targetUuid !== undefined ? (getMediaElementByUuid(state, targetUuid) as MediaText | undefined) : undefined;
    const targetAddress = targetUuid !== undefined ? getStructureIndexByElementUuid(state, targetUuid) : undefined;

    if (
      targetUuid !== undefined &&
      (!target || target.type !== 'text' || !targetAddress || targetUuid === elementUuid)
    ) {
      return;
    }

    // A personalization-locked layer is outside personalisation, so it has no
    // business in a group whose whole content is the buyer's. The target list
    // already leaves locked layers out and neither panel renders one, so this
    // closes the same door from the operation's side.
    //
    // Joining only — a layer locked after it joined must still be able to leave.
    if (target && (element.personalizationLocked || target.personalizationLocked)) {
      return;
    }

    // The pairing receivesSharedValue excludes, refused at the point it would
    // be created: both panels offer only "None" for it, and this means a third
    // caller cannot quietly build what every reader of that predicate then has
    // to work around.
    //
    // Only joining. Leaving is always allowed, or a template that already holds
    // the pair could never be untangled.
    //
    // The two layers in front of us, not the target's whole group: an imported
    // group already holding a varying member is not made worse by a third
    // ordinary layer joining it, and refusing would block a pairing of two
    // sound layers over a fault in a fourth that this operation cannot reach.
    // That member is inert either way — propagation skips it, the cap union
    // below leaves it out, and it resolves for itself rather than for the
    // group. What this guard is for is creating the pair, not tolerating one.
    if (target && (element.extra.textVariants || target.extra.textVariants)) {
      return;
    }

    // A layer the spread's condition group does not list is not a
    // personalisation field: it prints, but no panel ever draws the buyer an
    // input for it. Grouping one in gives the field a member whose rules cannot
    // be enforced — Required on it would mark the buyer's field required with
    // nothing to raise the error — and, if it were elected lead, the field
    // would vanish from the page entirely. The target list already withholds
    // these (`canJoin`), and until now that was the only thing stopping it;
    // this closes the door where the other two invariants are closed.
    //
    // Joining only. A layer taken OUT of the condition group after it joined
    // must still be able to leave, or it could never be untangled.
    if (
      target &&
      targetAddress &&
      (!getPersonalizationTextUuids(designData?.spreads[address.spreadIndex]).has(elementUuid) ||
        !getPersonalizationTextUuids(designData?.spreads[targetAddress.spreadIndex]).has(targetUuid as number))
    ) {
      return;
    }

    const sharedFieldId = target ? target.extra.sharedFieldId ?? uuidv4() : undefined;
    if (sharedFieldId === currentId) {
      return;
    }

    // A group of one is not shared. Leaving the id on the last member would
    // have the panel keep offering to unlink a field that is tied to nothing,
    // and would silently re-absorb the next element that joined it.
    const strandedMembers = currentId
      ? getSharedTextFieldMembers(designData, currentId).filter((member) => member.element.uuid !== elementUuid)
      : [];
    const loneLeftover = strandedMembers.length === 1 ? strandedMembers[0] : undefined;

    // The group the join produces, and the string it will hold.
    //
    // The strictest cap in a group holds for the whole of it, so a newcomer
    // whose own cap is tighter than the value it is adopting shortens that
    // value — for every member, because the group is one string. Without this
    // the buyer opens the page to an over-limit error on a value they never
    // typed, which is the failure this feature exists to prevent, arriving at
    // join time rather than at typing time. The same rule the author's cap
    // input already follows when it is tightened.
    //
    // Locked and hidden members are left out of the union for the reason
    // resolveSharedTextField leaves them out: neither reports a violation.
    const joinedMembers = target
      ? [
          element,
          ...(target.extra.sharedFieldId
            ? getSharedTextFieldMembers(designData, target.extra.sharedFieldId).map((member) => member.element)
            : [target]),
        ]
      : [];
    const caps = joinedMembers
      .filter((member) => receivesSharedValue(member) && !member.hidden)
      .map((member) => member.extra.maxCharCount)
      .filter((cap): cap is number => cap !== undefined);
    const groupCap = caps.length ? Math.min(...caps) : undefined;
    // Poured back through the format after the cut. A raw slice can leave the
    // value mid-pattern — a string applyValueFormat would never produce — and
    // the whole feature leans on the stored value being in the template's
    // canonical form.
    //
    // Unless the pouring undoes the cut: an emailDomain format re-appends the
    // whole domain, so a cap shorter than it cannot be met and canonical at
    // once. Fitting wins there, because fitting is what this truncation is
    // for; the author has set a cap their own format cannot satisfy, and they
    // see that on the layer either way.
    const fitToCap = (text: string, cap: number) => {
      const sliced = text.slice(0, cap);
      const poured = applyValueFormat(sliced, target?.extra.valueFormat);
      return poured.length <= cap ? poured : sliced;
    };
    const adoptedText =
      target && groupCap !== undefined ? fitToCap(target.extra.text, groupCap) : target?.extra.text ?? '';
    const truncated = !!target && adoptedText !== target.extra.text;

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Shared field updated'));

      if (loneLeftover) {
        dispatch(
          updateMediaElementAction({
            elementAddress: loneLeftover.address,
            elementUpdate: { extra: { ...loneLeftover.element.extra, sharedFieldId: undefined } },
          }),
        );
      }

      const sampleFlagChangeAllowed = isSampleFlagChangeAllowed(state);
      const spreadsToReflow = new Set<number>();
      const noteReflow = (spreadIndex: number, uuid: number, from: string, to: string) => {
        if (from !== to && isInReflowRow(designData, spreadIndex, uuid)) {
          spreadsToReflow.add(spreadIndex);
        }
      };

      const elementUpdate: Partial<MediaText> = target
        ? buildSharedTextValueUpdate(element, adoptedText, sampleFlagChangeAllowed, target.extra.valueFormat)
        : { extra: { ...element.extra } };
      elementUpdate.extra = { ...elementUpdate.extra!, sharedFieldId };
      dispatch(updateMediaElementAction({ elementAddress: address, elementUpdate }));
      if (target) {
        noteReflow(address.spreadIndex, elementUuid, element.extra.text, adoptedText);
      }

      // The target is rewritten when it had no group yet — it is becoming one
      // half of a new one — and also whenever the newcomer's cap shortened the
      // value, because a group that kept two different strings would be the
      // very drift this exists to prevent.
      if (target && targetAddress && (!target.extra.sharedFieldId || truncated)) {
        dispatch(
          updateMediaElementAction({
            elementAddress: targetAddress,
            // The spread is there for what buildSharedTextValueUpdate adds
            // AROUND `extra` — the re-measured geometry and the sample/path
            // clean-ups. Its own `extra` is then replaced by the line below,
            // which rebuilds the identical value plus the group id.
            elementUpdate: {
              ...(truncated
                ? buildSharedTextValueUpdate(target, adoptedText, sampleFlagChangeAllowed, target.extra.valueFormat)
                : {}),
              extra: { ...target.extra, ...(truncated ? { text: adoptedText } : {}), sharedFieldId },
            },
          }),
        );
        noteReflow(targetAddress.spreadIndex, targetUuid as number, target.extra.text, adoptedText);
      }

      // The rest of the group the newcomer joined. Untouched on an ordinary
      // join; rewritten when its value had to be shortened to fit.
      //
      // Through the propagation operation rather than walked again here: this
      // is the same fan-out, and a second copy of it is where the variants
      // exclusion went missing once already. `batch` defers the notification,
      // not the dispatch, so the writes above are in the store and the walk
      // sees the newcomer already carrying the id and the adopted value — its
      // own no-change guard then skips it, and the target is the source.
      if (target && truncated && target.extra.sharedFieldId) {
        dispatch(
          syncSharedTextValueOperation(target.uuid, target.extra.sharedFieldId, {
            text: adoptedText,
            valueFormat: target.extra.valueFormat,
          }),
        );
      }

      // A changed string changes a measured width, so a row packed around the
      // outgoing one has to re-pack — once per spread, after every write on it.
      spreadsToReflow.forEach((spreadIndex) => dispatch(updateHiddenElementsOperation(spreadIndex)));
    });
  };

export default updateSharedTextFieldOperation;
